import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  ZONE, findSkinImage, fmt, signed, pct, pnlColor, rise,
  ZoneHead, ZoneStats, ZoneSearch, ZoneChips, ZoneSelect, ZoneEmpty, ZoneFooter,
} from './zoneShared';
import { rowName, dayOf, useSinceMoves, readingNote } from './priceArchive';
import ItemDetailModal, { DetailButton, TradeButton, hasTrade } from './ItemDetailModal';
import { buildStickerIndex, withStickerImages } from './helpers';
import StickerSlots from './StickerSlots';

// Sold Zone — the Tickets view (Sold 3) rebuilt on the Overview 7 board:
// perforated trade tickets with a candy stub and a WIN/LOSS stamp.
const SORTS = [
  { key: 'date',   label: 'Newest first' },
  { key: 'profit', label: 'Best profit' },
  { key: 'loss',   label: 'Worst profit' },
  { key: 'roi',    label: 'Best ROI' },
];

const FILTERS = [
  { key: 'all',  label: 'All' },
  { key: 'win',  label: 'Wins' },
  { key: 'loss', label: 'Losses' },
];

// The day the money landed — see SoldTab3 for why `timestamp` is the fallback.
const saleDay = row => dayOf(row?.csfloatSale?.soldAt || row?.soldAt || row?.timestamp);

export default function SoldTabZone({ rows, skinsData = [], livePrices, csfloatPrices, onEdit }) {
  const [query, setQuery]     = useState('');
  const [sort, setSort]       = useState('date');
  const [detail, setDetail]   = useState(null);   // the ticket whose modal is open
  const [outcome, setOutcome] = useState('all');

  // Sticker pictures: the row stores names, the catalogue has the art.
  const stickerIndex = useMemo(() => buildStickerIndex(skinsData), [skinsData]);

  const sold = useMemo(() => {
    return (rows || [])
      .map((row, realIndex) => ({ row, realIndex }))
      .filter(({ row }) => row.skin?.value && parseFloat(row.sellPrice) > 0)
      .map(e => {
        const buy    = parseFloat(e.row.buyPrice) || 0;
        const sell   = parseFloat(e.row.sellPrice) || 0;
        const profit = parseFloat(e.row.profit) || 0;
        const roi    = buy > 0 ? profit / buy * 100 : null;
        return { ...e, buy, sell, profit, roi, ts: new Date(e.row.timestamp) };
      });
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = sold.filter(e => {
      if (q && !e.row.skin.value.toLowerCase().includes(q)) return false;
      if (outcome === 'win'  && e.profit < 0) return false;
      if (outcome === 'loss' && e.profit >= 0) return false;
      return true;
    });
    const by = {
      date:   (a, b) => b.ts - a.ts,
      profit: (a, b) => b.profit - a.profit,
      loss:   (a, b) => a.profit - b.profit,
      roi:    (a, b) => (b.roi ?? -Infinity) - (a.roi ?? -Infinity),
    }[sort];
    return [...list].sort(by);
  }, [sold, query, sort, outcome]);

  // Execution (you against the market on the day) and timing (the market
  // since), both off the price archive. See SoldTab3 for the reasoning.
  const archivePoints = useMemo(() => sold.map(e => ({
    key: e.realIndex, name: rowName(e.row), date: saleDay(e.row),
  })), [sold]);
  const archive = useSinceMoves(archivePoints, csfloatPrices);

  const totals = useMemo(() => {
    const profit  = sold.reduce((s, e) => s + e.profit, 0);
    const revenue = sold.reduce((s, e) => s + e.sell, 0);
    const cost    = sold.reduce((s, e) => s + e.buy, 0);
    const wins    = sold.filter(e => e.profit >= 0).length;

    const market = sold.reduce((acc, e) => {
      const mv = archive.get(e.realIndex);
      if (!mv) return acc;
      return {
        n: acc.n + 1,
        edge: acc.edge + (e.sell - mv.then),
        drift: acc.drift + mv.change,
        early: acc.early + (mv.change > 0 ? 1 : 0),
      };
    }, { n: 0, edge: 0, drift: 0, early: 0 });

    return {
      profit, revenue, cost, wins, losses: sold.length - wins, market,
      winRate: sold.length ? Math.round(wins / sold.length * 100) : null,
      roi: cost > 0 ? profit / cost * 100 : null,
    };
  }, [sold, archive]);

  // Scoped to the current filter, so the toolbar answers "what am I looking at"
  // rather than repeating the lifetime figures in the header.
  const shown = useMemo(() => {
    const profit = filtered.reduce((s, e) => s + e.profit, 0);
    const wins   = filtered.filter(e => e.profit >= 0).length;
    return { profit, wins, losses: filtered.length - wins };
  }, [filtered]);

  if (!sold.length) {
    return (
      <div className="zn-root">
        <ZoneHead pre="THE" hl="TICKET" post="DESK" sub="Every sale you've made, printed as a trade ticket." />
        <ZoneEmpty>No sales recorded yet. Sell something and the tickets pile up here.</ZoneEmpty>
      </div>
    );
  }

  return (
    <div className="zn-root">

      <ZoneHead
        pre="THE" hl="TICKET" post="DESK"
        sub="One ticket per completed sale. Tear off the stub to see how it went. Click a ticket to edit the item."
        stickers={[
          { text: `${sold.length} tickets printed`, bg: ZONE.teal },
          { text: `${totals.wins}W / ${totals.losses}L`, bg: ZONE.lime },
        ]}
      />

      <ZoneStats items={[
        { label: 'Banked profit', value: signed(totals.profit),
          sub: totals.roi != null ? `${pct(totals.roi)} on $${fmt(totals.cost)} spent` : 'n/a',
          bg: totals.profit >= 0 ? ZONE.lime : ZONE.hot },
        { label: 'Revenue', value: `$${fmt(totals.revenue)}`, sub: `across ${sold.length} sales`, bg: ZONE.white },
        { label: 'Win rate', value: totals.winRate == null ? 'n/a' : `${totals.winRate}%`,
          sub: `${totals.wins} of ${sold.length} sales made money`, bg: ZONE.yellow },
        // Needs the price archive; hidden when nothing could be priced.
        totals.market.n > 0 && {
          label: 'Sold vs market', value: signed(totals.market.edge),
          sub: `against the CSFloat price on each sale day · ${totals.market.n} of ${sold.length} priced`,
          bg: totals.market.edge >= 0 ? ZONE.lime : ZONE.pink },
        totals.market.n > 0 && {
          label: 'Market since you sold', value: signed(totals.market.drift),
          sub: totals.market.drift > 0
            ? `${totals.market.early} of ${totals.market.n} would be worth more today`
            : 'holding would have left you worse off',
          // Inverted on purpose: a market that ran up after you sold is not a win.
          bg: totals.market.drift <= 0 ? ZONE.lime : ZONE.pink },
      ].filter(Boolean)} />

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="zn-bar">
        <ZoneSearch value={query} onChange={setQuery} placeholder="Search tickets…" />
        <ZoneChips options={FILTERS} value={outcome} onChange={setOutcome} />
        <span className="zn-count" style={{ marginLeft: 'auto' }}>
          {filtered.length} ticket{filtered.length !== 1 ? 's' : ''} · {shown.wins}W/{shown.losses}L ·{' '}
          <span style={{ color: pnlColor(shown.profit) }}>{signed(shown.profit)}</span>
        </span>
        <ZoneSelect value={sort} onChange={setSort} options={SORTS} label="Sort tickets" />
      </div>

      {/* ── Tickets ────────────────────────────────── */}
      {filtered.length === 0 ? (
        <ZoneEmpty>No tickets match your filters.</ZoneEmpty>
      ) : (
        <div className="zn-ticket-grid">
          {filtered.map((e, i) => {
            const img = findSkinImage(skinsData, e.row.skin.value, e.row.condition?.value);
            const win = e.profit >= 0;
            return (
              <motion.div key={e.realIndex} className="zn-ticket"
                style={{ '--zn-stub': win ? ZONE.lime : ZONE.pink }}
                onClick={() => onEdit && onEdit(e.row, e.realIndex)}
                {...rise(i)}>

                <div className="zn-ticket-main">
                  <DetailButton onOpen={() => setDetail({ row: e.row, img })} />
                  {/* Only on items with a trade behind them: the card click opens
                      the editor, so this is the way in to the trade overview. */}
                  {hasTrade(e.row) && (
                    <TradeButton className="" onOpen={() => setDetail({ row: e.row, img })} />
                  )}
                  <div className="zn-ticket-date">
                    {e.ts.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    {e.row.siteFee?.label ? ` · ${e.row.siteFee.label.split(' ')[0]}` : ''}
                  </div>

                  <div className="zn-ticket-body">
                    {img
                      ? <img src={img} alt="" />
                      : <div className="zn-thumb-blank" style={{ width: 62, height: 44 }} />}
                    <div style={{ minWidth: 0 }}>
                      <div className="zn-ticket-name" title={e.row.skin.value}>{e.row.skin.value}</div>
                      <div className="zn-ticket-sub">
                        {e.row.condition?.value || 'n/a'}
                        {e.row.receivedFromTradeId ? ' · from trade' : ''}
                      </div>
                      {(() => {
                        const mv = archive.get(e.realIndex);
                        if (!mv) return null;
                        return (
                          <div className="zn-ticket-sub" title={readingNote(mv)}
                            style={{ color: pnlColor(-mv.change) }}>
                            market {signed(mv.change)} ({pct(mv.pct)}) since · was ${fmt(mv.then)}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* What was on it when it sold. Unlike the locker cards, a
                      ticket with no stickers draws nothing rather than holding
                      the row open: most sales are plain items, and a column of
                      empty strips would be all the eye sees. */}
                  <StickerSlots
                    stickers={withStickerImages(e.row.stickers, stickerIndex)}
                    charms={e.row.charms}
                    prices={livePrices} variant="zone" maxWidth={186}
                    style={{ marginTop: 13, marginBottom: 0 }} />

                  <div className="zn-ticket-route">
                    <b>${fmt(e.buy)}</b>
                    <span className="track" />
                    <b>${fmt(e.sell)}</b>
                  </div>
                </div>

                <div className="zn-ticket-perf" aria-hidden="true" />

                <div className="zn-ticket-stub">
                  <span className="zn-stamp">{win ? 'WIN' : 'LOSS'}</span>
                  <div className="zn-ticket-profit" style={{ color: pnlColor(e.profit) }}>{signed(e.profit)}</div>
                  {e.roi != null && (
                    <div className="zn-ticket-roi" style={{ color: pnlColor(e.profit) }}>{pct(e.roi)}</div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <ZoneFooter stickers={[
        { text: 'fees included in every profit', bg: ZONE.purple },
        { text: `lifetime ${signed(totals.profit)}`, bg: totals.profit >= 0 ? ZONE.lime : ZONE.hot, right: true },
      ]} />

      {detail && (
        <ItemDetailModal
          variant="zone"
          kind="sold"
          name={detail.row.skin?.value}
          condition={detail.row.condition?.value}
          image={detail.img}
          history={detail.row.priceHistory}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          row={detail.row}
          rows={rows || []}
          /* the trade overview draws a thumbnail for every item on both sides */
          skinsData={skinsData}
          /* click-through: any item in the trade chain re-centres this modal */
          onOpenRow={r => setDetail({ row: r, img: findSkinImage(skinsData, r.skin?.value, r.condition?.value) })}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
